import { FC, SyntheticEvent, useCallback, useLayoutEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { sortByOrder } from 'utils/collection-manipulation/array-sort-by';
import { EIconSize } from 'utils/constants/app/ui';
import SpringConfigs from 'utils/constants/swarm/spring-configs';
import { getMountPath } from 'utils/generic/route-manipulation';
import { ENotRealSports } from 'interfaces/not-real-sports';
import { Sport } from 'interfaces/sportsbook-data-levels';
import { CompetitionItemCollapse } from 'newelements/Sportsbook/layouts/__partials/BoostedOdds/CompetitionItemCollapse';
import { EventCollapse } from 'components/EventCollapse';
import { GlobalIcon } from 'components/GlobalIcon';
import { NoItem } from 'components/Shared/NoItem';
import { TopMatchesCompactMenuSkeleton } from 'newcomponents/Shared/sportsbook/TopMatchesCompactMenuItem/Skeleton';
import { useSbRouteMatch } from 'newhooks/generic/useSbRouteMatch';
import { setSportsbookSelectedSport } from 'store/actions/new-sportsbook';
import { getSportsbookSelectedSport } from 'store/selectors/new-sportsbook';
import './index.less';

if (SpringConfigs.IS_RTL) {
  import('./rtl.less');
}

type TProps = {
  sportData: Record<string, Sport>;
  fromSportsList?: boolean;
  openFromSideBarPopup?: boolean;
};

export const TopMatchesCompactLayout: FC<TProps> = ({ sportData }) => {
  const selectedSport = useSelector(getSportsbookSelectedSport);

  const dispatch = useDispatch();
  const history = useHistory();
  const { t } = useTranslation();
  const { path, params } = useSbRouteMatch();
  const currentSportAlias = selectedSport?.alias || params?.sport;

  useLayoutEffect(() => {
    const selectedSportToUpdate = Object.values(sportData).find(
      sport => sport.alias === currentSportAlias
    );

    if (
      selectedSportToUpdate &&
      selectedSportToUpdate?.alias !== selectedSport?.alias
    ) {
      dispatch(setSportsbookSelectedSport(selectedSport));
    }
  }, [sportData, currentSportAlias]);

  const handleMatchCardClick = useCallback(
    (
      _e: SyntheticEvent,
      _region: string,
      _competitionId: number,
      gameId: number
    ) => {
      history.push(
        `${getMountPath(path)}/${ENotRealSports.topMatches}/${gameId}`
      );
    },
    [path]
  );

  if (!sportData) {
    return <TopMatchesCompactMenuSkeleton />;
  }

  if (Object.keys(sportData).length === 0) {
    return (
      <NoItem
        title={t('sportsbook.noGame')}
        text={t('sportsbook.noGameText')}
      />
    );
  }

  return (
    <div className="topMatchesCompetitionsByLists">
      <div className="topMatchesCompetitionsByLists__content">
        {sortByOrder(Object.values(sportData)).map(
          (sport: Sport, sportIndex) => (
            <EventCollapse
              defaultActive={!sportIndex}
              key={sport.id}
              title={
                <span className="sportCollapseTitle">
                  <GlobalIcon
                    name={sport.alias}
                    lib="sports"
                    size={EIconSize._24}
                  />
                  {sport.name}
                </span>
              }
            >
              <>
                {sortByOrder(Object.values(sport.region || {})).map(
                  (region, regIndex) =>
                    sortByOrder(Object.values(region.competition || {})).map(
                      (comp, compIndex) => (
                        <CompetitionItemCollapse
                          key={comp.id}
                          comp={comp}
                          regionAlias={region.alias}
                          sportInfo={{
                            name: sport.name,
                            alias: sport.alias
                          }}
                          handleMatchCardClick={handleMatchCardClick}
                          isFirstCompetition={
                            regIndex === 0 &&
                            compIndex === 0 &&
                            sportIndex === 0
                          }
                          isBoostedOdd
                        />
                      )
                    )
                )}
              </>
            </EventCollapse>
          )
        )}
      </div>
    </div>
  );
};
